import axios from "axios";
import { getCustomerGeoFor } from "@/lib/customerGeo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, withCredentials: true });
// Attach the customer's session GPS point to shop distance requests (only when it belongs to the active PIN).
client.interceptors.request.use((cfg) => {
  if ((cfg.method || "get") === "get" && /^\/(shops($|\/)|products($|\/)|services($|\/)|hiring($|\/)|search$|category-counts$|public\/vendors\/)/.test(cfg.url || "") && cfg.params?.pincode) {
    const g = getCustomerGeoFor(cfg.params.pincode);
    if (g) cfg.params = { ...cfg.params, lat: g.lat, lng: g.lng };
  }
  return cfg;
});
// Backend restarts (preview wake-up / hot reload) briefly return 502-504: retry idempotent GETs twice, then tell the user.
const RETRY_STATUS = new Set([502, 503, 504]);
const RETRY_DELAYS = [600, 1500];
let lastBusyToast = 0;
client.interceptors.response.use(undefined, async (error) => {
  if (error.response?.status === 503 && error.response?.data?.maintenance) {
    window.dispatchEvent(new Event("vb:maintenance"));
    return Promise.reject(error);
  }
  const cfg = error.config || {};
  const status = error.response?.status;
  const transient = RETRY_STATUS.has(status) || (!error.response && error.code !== "ERR_CANCELED");
  if (transient && (cfg.method || "get").toLowerCase() === "get") {
    cfg.__retry = (cfg.__retry || 0) + 1;
    if (cfg.__retry <= RETRY_DELAYS.length) {
      console.warn(`[api] ${status || error.code} on GET ${cfg.url} — retry ${cfg.__retry}/${RETRY_DELAYS.length}`);
      await new Promise((r) => setTimeout(r, RETRY_DELAYS[cfg.__retry - 1]));
      return client.request(cfg);
    }
    console.error(`[api] GET ${cfg.url} failed after retries (${status || error.code})`);
    if (!cfg.__quiet && Date.now() - lastBusyToast > 8000) {
      lastBusyToast = Date.now();
      import("sonner").then(({ toast }) => toast.error("Server abhi busy hai, dobara try karein."));
    }
  }
  return Promise.reject(error);
});

export default client;

/** Resolve a stored image URL to a full absolute URL. Returns absolute URLs unchanged. */
// 1x1 transparent SVG — used as fallback when the item has no uploaded image.
// Keeps React happy (no empty-string src warning) without a network round-trip.
export const IMG_PLACEHOLDER =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'><rect width='1' height='1' fill='%23f1f5f9'/></svg>";

export function resolveImageUrl(src) {
  if (!src) return IMG_PLACEHOLDER;
  if (/^https?:\/\//i.test(src)) return src;
  if (src.startsWith("/api/")) return `${BACKEND_URL}${src}`;
  if (src.startsWith("/")) return `${BACKEND_URL}${src}`;
  return src;
}

// ---- Auth ----
export const authRegister = (payload) => client.post("/auth/register", payload).then((r) => r.data);
export const areaSearch = (q) => client.get("/geocode/area-search", { params: { q } }).then((r) => r.data);
export const otpStatus = () => client.get("/auth/otp/status").then((r) => r.data);
export const signupStart = (payload) => client.post("/auth/signup/start", payload).then((r) => r.data);
export const signupResend = (signup_id, channel) => client.post("/auth/signup/resend", { signup_id, channel }).then((r) => r.data);
export const signupVerify = (signup_id, channel, otp) => client.post("/auth/signup/verify", { signup_id, channel, otp }).then((r) => r.data);
export const passwordForgot = (identifier) => client.post("/auth/password/forgot", { identifier }).then((r) => r.data);
export const passwordResend = (reset_id) => client.post("/auth/password/resend", { reset_id }).then((r) => r.data);
export const passwordVerify = (reset_id, otp) => client.post("/auth/password/verify", { reset_id, otp }).then((r) => r.data);
export const passwordReset = (reset_token, new_password) => client.post("/auth/password/reset", { reset_token, new_password }).then((r) => r.data);
export const authLogin = (payload) => client.post("/auth/login", payload).then((r) => r.data);
export const authLogout = () => client.post("/auth/logout").then((r) => r.data);
export const authMe = () => client.get("/auth/me").then((r) => r.data);

// Unified profile
export const getProfile = () => client.get("/profile").then((r) => r.data);
export const getPrivacy = () => client.get("/me/privacy").then((r) => r.data);
export const savePrivacy = (body) => client.put("/me/privacy", body).then((r) => r.data);
export const updateProfile = (patch) => client.patch("/profile", patch).then((r) => r.data);
export const createProduct = (payload) => client.post("/products", payload).then((r) => r.data);

// ---- Vendor ----
export const vendorApply = (payload) => client.post("/vendor/apply", payload).then((r) => r.data);

// ---- Public ----
export const fetchCategories = () => client.get("/categories").then((r) => r.data);
export const fetchPincodeInfo = (pin) => client.get(`/pincodes/${pin}`).then((r) => r.data);
export const fetchPincodes = () => client.get("/pincodes").then((r) => r.data);
export const reverseGeocode = (lat, lng) => client.get("/geocode/reverse", { params: { lat, lng } }).then((r) => r.data);
export const createLocationShare = (payload) => client.post("/location-shares", payload).then((r) => r.data);
export const fetchMyLocationShares = (service_id) => client.get("/location-shares/mine", { params: { service_id }, __quiet: true }).then((r) => r.data);
export const revokeLocationShare = (id) => client.delete(`/location-shares/${id}`).then((r) => r.data);
export const fetchReceivedLocationShares = () => client.get("/location-shares/received", { __quiet: true }).then((r) => r.data);
export const fetchProviderLocationShares = (service_id) => client.get("/location-shares/from-provider", { params: { service_id }, __quiet: true }).then((r) => r.data);
export const replyLocationShare = (id, payload) => client.post(`/location-shares/${id}/reply`, payload).then((r) => r.data);
export const fetchLocationPulse = () => client.get("/location-shares/pulse", { __quiet: true }).then((r) => r.data);
export const fetchLocationInbox = () => client.get("/location-shares/inbox", { __quiet: true }).then((r) => r.data);
export const fetchLocationShareDetail = (id) => client.get(`/location-shares/${id}`, { __quiet: true }).then((r) => r.data);
export const fetchLocationShareDirections = (id) => client.get(`/location-shares/${id}/directions`, { __quiet: true }).then((r) => r.data);
export const nearestPincodes = (lat, lng, limit = 3, exclude) => client.get("/geocode/nearest-pincodes", { params: { lat, lng, limit, exclude } }).then((r) => r.data);

// Multi-image product
export const uploadImage = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const vendorUpdateProductImages = (id, images, cover_index = 0) =>
  client.patch(`/vendor/products/${id}/images`, { images, cover_index }).then((r) => r.data);

// Vendor reviews
export const vendorReviews = (slug, limit = 20) =>
  client.get(`/vendors/${slug}/reviews`, { params: { limit } }).then((r) => r.data);
export const postVendorReview = (slug, payload) =>
  client.post(`/vendors/${slug}/reviews`, payload).then((r) => r.data);
export const reviewEligibility = (slug) =>
  client.get(`/me/review-eligibility/${slug}`).then((r) => r.data);
export const serviceReviewApi = (id) => ({
  list: () => client.get(`/services/${id}/reviews`).then((r) => r.data),
  post: (payload) => client.post(`/services/${id}/reviews`, payload).then((r) => r.data),
  eligibility: () => client.get(`/me/service-review-eligibility/${id}`).then((r) => r.data),
  reply: (reviewId, reply) => client.post(`/services/${id}/reviews/${reviewId}/reply`, { reply }).then((r) => r.data),
});
export const replyVendorReview = (slug, reviewId, reply) =>
  client.post(`/vendors/${slug}/reviews/${reviewId}/reply`, { reply }).then((r) => r.data);

// Admin pincodes
export const adminListPincodes = (search = "") =>
  client.get("/admin/pincodes", { params: search ? { search } : {} }).then((r) => r.data);
export const adminListPinImports = (limit = 20, skip = 0) =>
  client.get("/admin/pincodes/imports", { params: { limit, skip } }).then((r) => r.data);
export const adminCreatePincode = (p) => client.post("/admin/pincodes", p).then((r) => r.data);
export const adminUpdatePincode = (pin, p) => client.patch(`/admin/pincodes/${pin}`, p).then((r) => r.data);
export const adminTogglePincode = (pin, active) =>
  client.post(`/admin/pincodes/${pin}/toggle`, { active }).then((r) => r.data);
export const adminDeletePincode = (pin) => client.delete(`/admin/pincodes/${pin}`).then((r) => r.data);
export const adminBulkImportPincodes = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post("/admin/pincodes/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const fetchProducts = (params = {}) => client.get("/products", { params }).then((r) => r.data);
export const fetchProduct = (id, pincode) => client.get(`/products/${id}`, { params: { pincode } }).then((r) => r.data);
export const fetchServices = (params = {}) => client.get("/services", { params }).then((r) => r.data);
export const fetchService = (id, pincode) => client.get(`/services/${id}`, { params: { pincode } }).then((r) => r.data);
export const fetchHiring = (params = {}) => client.get("/hiring", { params }).then((r) => r.data);
export const fetchHire = (id) => client.get(`/hiring/${encodeURIComponent(id)}`).then((r) => r.data);
export const fetchShops = (params = {}) => client.get("/shops", { params }).then((r) => r.data);
export const fetchReorderShops = () => client.get("/me/reorder-shops").then((r) => r.data);
export const trackWhatsAppOrder = (product_id) => client.post("/events/whatsapp-order", { product_id }).catch(() => {});
const _contactFired = {};
// Fire-and-forget; server dedupes per visitor for 30 min, this only drops instant double-fires.
export const trackShopContact = (shop_id, kind) => {
  const k = `${shop_id}:${kind}`;
  if (!shop_id || Date.now() - (_contactFired[k] || 0) < 2000) return;
  _contactFired[k] = Date.now();
  client.post("/events/shop-contact", { shop_id, kind }).catch(() => {});
};
export const fetchCategoryCounts = (params = {}) => client.get("/category-counts", { params }).then((r) => r.data);
export const fetchShop = (id, pincode) => client.get(`/shops/${id}`, { params: { pincode } }).then((r) => r.data);
export const fetchShopStatus = (id) => client.get(`/shops/${id}/status`).then((r) => r.data);
export const fetchPublicVendor = (slug, pincode) => client.get(`/public/vendors/${slug}`, { params: { pincode } }).then((r) => r.data);
export const followVendor = (slug) => client.post(`/vendors/${slug}/follow`).then((r) => r.data);
export const unfollowVendor = (slug) => client.delete(`/vendors/${slug}/follow`).then((r) => r.data);
export const myFollows = () => client.get("/me/follows").then((r) => r.data);
export const myLikes = () => client.get("/me/likes").then((r) => r.data);
export const myComments = () => client.get("/me/comments").then((r) => r.data);
export const createServiceRequest = (term, pincode) => client.post("/service-requests", { term, pincode }).then((r) => r.data);
export const fetchLocalDemand = () => client.get("/vendor/local-demand").then((r) => r.data);
export const searchAll = (params = {}) => client.get("/search", { params }).then((r) => r.data);

// Advertisements
export const vendorCreateAd = (payload) => client.post("/vendor/ads", payload).then((r) => r.data);
export const vendorPayAd = (adId) => client.post(`/vendor/ads/${adId}/pay`).then((r) => r.data);
export const vendorListAds = () => client.get("/vendor/ads").then((r) => r.data);
export const vendorPauseAd = (id) => client.post(`/vendor/ads/${id}/pause`).then((r) => r.data);
export const vendorResumeAd = (id) => client.post(`/vendor/ads/${id}/resume`).then((r) => r.data);
export const adminListAds = (status = "") => client.get("/admin/ads", { params: status ? { status } : {} }).then((r) => r.data);
export const adminApproveAd = (adId, note = "") => client.post(`/admin/ads/${adId}/approve`, { note }).then((r) => r.data);
export const adminRejectAd = (adId, note = "") => client.post(`/admin/ads/${adId}/reject`, { note }).then((r) => r.data);
export const adminPauseAd = (adId, note = "") => client.post(`/admin/ads/${adId}/pause`, { note }).then((r) => r.data);
export const fetchNotifications = (pincode) => client.get("/notifications", { params: pincode ? { pincode } : {} }).then((r) => r.data);

// Cart (server-side persistence)
export const fetchMyCart = () => client.get("/me/cart").then((r) => r.data);
export const replaceMyCart = (items) => client.put("/me/cart", { items }).then((r) => r.data);
export const mergeMyCart = (items) => client.post("/me/cart/merge", { items }).then((r) => r.data);

// Wishlist (server-side persistence)
export const fetchMyWishlist = () => client.get("/me/wishlist").then((r) => r.data);
export const replaceMyWishlist = (ids) => client.put("/me/wishlist", { ids }).then((r) => r.data);

// Vendor delivery
export const fetchMyShops = () => client.get("/vendor/shops").then((r) => r.data);
export const vendorUpdateDelivery = (shopId, payload) => client.patch(`/vendor/shops/${shopId}/delivery`, payload).then((r) => r.data);

// Helper: extract error message
export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return [502, 503, 504].includes(err?.response?.status) || (err && !err.response) ? "Server abhi busy hai, dobara try karein." : err?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e?.msg || JSON.stringify(e)).replace(/^Value error, /, "")).join(", ");
  if (d?.msg) return d.msg;
  return String(d);
}

// Activities / flexible profiles
export const fetchMyActivities = () => client.get("/me/activities").then((r) => r.data);
export const saveMyActivities = (activities) => client.put("/me/activities", { activities }).then((r) => r.data);
export const saveActivityProfile = (kind, data) => client.put(`/me/activities/${kind}`, data).then((r) => r.data);
export const fetchPublicActivityProfile = (id) => client.get(`/p/${id}`).then((r) => r.data);
export const fetchMyJobs = () => client.get("/me/jobs").then((r) => r.data);
export const createMyJob = (job) => client.post("/me/jobs", job).then((r) => r.data);
export const updateMyJob = (id, job) => client.put(`/me/jobs/${id}`, job).then((r) => r.data);
export const deleteProfessionalListing = () => client.delete("/me/professional-listing").then((r) => r.data);
export const deleteMyJob = (id) => client.delete(`/me/jobs/${id}`).then((r) => r.data);
export const updateMyJobVisibility = (id, active) => client.patch(`/me/jobs/${id}/visibility`, { active }).then((r) => r.data);
export const updateMyJobStatus = (id, vacancy_status) => client.patch(`/me/jobs/${id}/status`, { vacancy_status }).then((r) => r.data);
export const verifyEmailStatus = () => client.get("/auth/verify-email/status").then((r) => r.data);
export const verifyEmailStart = () => client.post("/auth/verify-email/start").then((r) => r.data);
export const verifyEmailConfirm = (otp) => client.post("/auth/verify-email/confirm", { otp }).then((r) => r.data);
export const fetchJobEligibility = () => client.get("/me/jobs/eligibility").then((r) => r.data);
export const adminActivityReviews = () => client.get("/admin/activity-reviews").then((r) => r.data);
export const adminReviewItem = (kind, id, action) => client.post(`/admin/activity-reviews/${kind}/${id}`, { action }).then((r) => r.data);
export const uploadAttachment = (file, purpose = "ad") => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post("/uploads/attachment", fd, { params: { purpose }, headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const fileUrl = (url) => (url && url.startsWith("/api/") ? `${BACKEND_URL}${url}` : url);
export const fetchUnreadCount = (pincode) => client.get("/notifications/unread-count", { params: pincode ? { pincode } : {} }).then((r) => r.data);
export const fetchAdStatusAlerts = () => client.get("/notifications/ad-status").then((r) => r.data);
export const markNotificationsRead = (ids) => client.post("/notifications/read", { ids }).then((r) => r.data);
export const saveLanguagePref = (language) => client.put("/me/language", { language }).then((r) => r.data);
export const fetchAdSettings = () => client.get("/ads/settings").then((r) => r.data);
export const fetchAdModeNotice = () => client.get("/vendor/ads/mode-notice").then((r) => r.data.notice);
export const fetchSiteStatus = () => client.get("/site/status").then((r) => r.data);
export const adminGetSite = () => client.get("/admin/site").then((r) => r.data);
export const adminSetSite = (body) => client.put("/admin/site", body).then((r) => r.data);
export const dismissAdModeNotice = (id) => client.post(`/vendor/ads/mode-notice/${encodeURIComponent(id)}/dismiss`).then((r) => r.data);
export const adminSaveAdSettings = (s) => client.put("/admin/ads/settings", s).then((r) => r.data);
export const fetchAdPinLookup = (pins) => client.get("/ads/pin-lookup", { params: { pins } }).then((r) => r.data);
const rangeParams = (r = {}) => Object.fromEntries(Object.entries({ from: r.from, to: r.to }).filter(([, v]) => v));
export const fetchAdReport = (id, range) => client.get(`/vendor/ads/${id}/report`, { params: rangeParams(range) }).then((r) => r.data);
export const fetchPublicAd = (id) => client.get(`/ads/view/${id}`).then((r) => r.data);
export const trackAdClick = (id, pincode, placement = "notifications") => client.post(`/ads/${id}/click`, { pincode, placement }).catch(() => {});
export const trackAdView = (id, pincode, placement) => client.post(`/ads/${id}/view`, { pincode, placement }).then((r) => r.data).catch(() => null);
export const trackAdWhatsApp = (item, placement) => {
  if (item?.sponsored && item.sponsored_ad_id && item.sponsored_pincode) client.post(`/ads/${item.sponsored_ad_id}/whatsapp`, { pincode: item.sponsored_pincode, placement }).catch(() => {});
};
export const fetchAdminStats = () => client.get("/admin/stats").then((r) => r.data);
export const fetchAdminDailyStats = (days) => client.get("/admin/stats/daily", { params: { days } }).then((r) => r.data);
export const fetchAdminExpiredJobs = () => client.get("/admin/expired-jobs").then((r) => r.data);
export const fetchAdminShopAlerts = () => client.get("/admin/shop-alerts").then((r) => r.data);
export const markAdminShopAlert = (id) => client.post(`/admin/shop-alerts/${id}/seen`, { seen: true }).then((r) => r.data);
const recentAdClicks = new Map();
export const trackSponsoredClick = (item, placement) => {
  if (!item?.sponsored || !item.sponsored_ad_id || !item.sponsored_pincode) return;
  const key = `${item.sponsored_ad_id}:${item.sponsored_pincode}:${placement}`;
  if (Date.now() - (recentAdClicks.get(key) || 0) < 5000) return;
  recentAdClicks.set(key, Date.now());
  trackAdClick(item.sponsored_ad_id, item.sponsored_pincode, placement);
};
export const downloadAdReportCsv = (id, range) => downloadAdReport(id, range, "csv");
export const downloadAdReportPdf = (id, range) => downloadAdReport(id, range, "pdf");
const downloadAdReport = async (id, range, ext) => {
  const r = await client.get(`/vendor/ads/${id}/report.${ext}`, { params: rangeParams(range), responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url; a.download = `verb-ad-report-${id}${range?.from || range?.to ? `_${range.from || "start"}_to_${range.to || "today"}` : ""}.${ext}`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
export const compareAds = (a, b) => client.get("/vendor/ads/compare", { params: { a, b } }).then((r) => r.data);
export const createReportShare = (id) => client.post(`/vendor/ads/${id}/share`).then((r) => r.data);
export const listReportShares = (id) => client.get(`/vendor/ads/${id}/shares`).then((r) => r.data);
export const revokeReportShare = (shareId) => client.post(`/vendor/ads/shares/${shareId}/revoke`).then((r) => r.data);
export const fetchSharedReport = (token) => client.get(`/public/ad-reports/${token}`).then((r) => r.data);
export const fmtCtr = (v) => (v === null || v === undefined ? "N/A" : `${Number(v).toFixed(2)}%`);
export const copyLibraryImage = (url) => client.post("/uploads/library-copy", { url }).then((r) => r.data);
export const previewLibraryMigration = () => client.get("/admin/library-migration").then((r) => r.data);
export const runLibraryMigration = () => client.post("/admin/library-migration", { confirm: true }).then((r) => r.data);

export const fetchLocationMyServices = () => client.get("/location-shares/my-services", { __quiet: true }).then((r) => r.data);
export const searchLocationCustomers = (q) => client.get("/location-shares/customers", { params: { q }, __quiet: true }).then((r) => r.data);
export const sendLocationToCustomer = (payload) => client.post("/location-shares/send", payload).then((r) => r.data);
export const fetchSentLocationShares = () => client.get("/location-shares/sent", { __quiet: true }).then((r) => r.data);
